// src/pages/Platform.jsx
// PLATFORM OWNER panel (Kassem only) — manage all tenants:
// activate paid subscriptions, change status, DELETE test databases.
// Guarded by the PLATFORM_ADMIN_KEY (.env on the API) sent as x-admin-key.
import React, { useState, useCallback } from 'react';
import api from '../config/api';

const GREEN = '#2e9f86';
const KEY_STORAGE = 'mmm_platform_key';

const STATUS_STYLE = {
  trial:     { bg: '#fef3c7', color: '#92400e' },
  active:    { bg: '#d1fae5', color: '#065f46' },
  expired:   { bg: '#fee2e2', color: '#991b1b' },
  suspended: { bg: '#e5e7eb', color: '#374151' },
};

const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

export default function Platform() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || '');
  const [keyInput, setKeyInput] = useState('');
  const [tenants, setTenants] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('');

  const headers = useCallback((k) => ({ 'x-admin-key': k || key }), [key]);

  const load = useCallback(async (k) => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/api/platform/tenants', { headers: headers(k) });
      setTenants(res.data);
      if (k) { setKey(k); sessionStorage.setItem(KEY_STORAGE, k); }
    } catch (err) {
      if (err.response?.status === 401) {
        setKey(''); sessionStorage.removeItem(KEY_STORAGE);
        setError('Wrong admin key.');
      } else setError(err.response?.data?.error || err.message);
      setTenants(null);
    } finally { setLoading(false); }
  }, [headers]);

  // first load if key already in session
  React.useEffect(() => { if (key && tenants === null && !loading && !error) load(); }, [key, tenants, loading, error, load]);

  const activate = async (t) => {
    const months = Number(window.prompt(`Activate "${t.company_name}" for how many months?`, '12'));
    if (!months || months <= 0) return;
    setBusyId(t._id);
    try {
      const res = await api.post(`/api/platform/tenants/${t._id}/activate`, { months }, { headers: headers() });
      alert(`✓ ${t.company_name} active until ${fmtD(res.data.expires_at)}`);
      await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setBusyId(null); }
  };

  // paid AI add-ons — all OFF by default; only the platform owner switches them on
  const setAddon = async (t, key, on) => {
    setBusyId(t._id);
    try {
      await api.post(`/api/platform/tenants/${t._id}/addons`, { [key]: on }, { headers: headers() });
      await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setBusyId(null); }
  };

  const setStatus = async (t, status) => {
    if (!window.confirm(`Set "${t.company_name}" to ${status.toUpperCase()}?`)) return;
    setBusyId(t._id);
    try {
      await api.post(`/api/platform/tenants/${t._id}/status`, { status }, { headers: headers() });
      await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setBusyId(null); }
  };

  const remove = async (t) => {
    const typed = window.prompt(
      `⚠️ PERMANENTLY DELETE "${t.company_name}"?\n\nThis drops database ${t.db_name} with ${t.stats?.documents ?? '?'} documents and ${t.stats?.users ?? '?'} users. This CANNOT be undone.\n\nType the company name to confirm:`
    );
    if (typed === null) return;
    if (typed.trim() !== (t.company_name || '').trim()) { alert('Name did not match — nothing deleted.'); return; }
    setBusyId(t._id);
    try {
      const res = await api.delete(`/api/platform/tenants/${t._id}`, { headers: headers() });
      alert(`✓ Deleted ${res.data.company_name} (${res.data.dropped})`);
      await load();
    } catch (err) { alert(err.response?.data?.error || err.message); }
    finally { setBusyId(null); }
  };

  // ── key gate ──
  if (!key) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
        <form onSubmit={(e) => { e.preventDefault(); if (keyInput.trim()) load(keyInput.trim()); }}
              style={{ background: '#1e293b', borderRadius: 16, padding: 32, width: 360, maxWidth: '92vw' }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 20, marginBottom: 6 }}>🔐 Platform Admin</div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 18 }}>Owner access only.</div>
          <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="Admin key" autoFocus
                 style={{ width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1.5px solid #334155', borderRadius: 9, padding: '11px 13px', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 12 }} />
          {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ width: '100%', background: GREEN, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Checking…' : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  const list = (tenants || []).filter(t => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (t.company_name || '').toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q) || (t.db_name || '').includes(q) || (t.status || '').includes(q);
  });
  const counts = (tenants || []).reduce((a, t) => { a[t.status] = (a[t.status] || 0) + 1; return a; }, {});

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: '24px 16px', fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 22 }}>🔐 Platform Admin</div>
          <span style={{ color: '#64748b', fontSize: 13 }}>{tenants?.length ?? 0} tenants · {counts.trial || 0} trial · {counts.active || 0} active · {counts.expired || 0} expired · {counts.suspended || 0} suspended</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={() => load()} disabled={loading} style={{ background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>↻ Refresh</button>
            <button onClick={() => { setKey(''); sessionStorage.removeItem(KEY_STORAGE); setTenants(null); }} style={{ background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>Lock</button>
          </div>
        </div>

        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by company, email, db, status…"
               style={{ width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', borderRadius: 9, padding: '10px 13px', color: '#fff', fontSize: 14, outline: 'none', marginBottom: 14 }} />

        {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {loading && <div style={{ color: '#94a3b8', padding: 30, textAlign: 'center' }}>Loading…</div>}

        {!loading && list.map(t => {
          const st = STATUS_STYLE[t.status] || STATUS_STYLE.suspended;
          const exp = t.status === 'trial' ? t.trial_ends_at : t.expires_at;
          const dl = daysLeft(exp);
          const busy = busyId === t._id;
          return (
            <div key={t._id} style={{ background: '#1e293b', borderRadius: 14, padding: '16px 18px', marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', opacity: busy ? 0.6 : 1 }}>
              <div style={{ minWidth: 200, flex: '1 1 220px' }}>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{t.company_name || '(no name)'}</div>
                <div style={{ color: '#94a3b8', fontSize: 12.5 }}>{t.email}</div>
                <div style={{ color: '#475569', fontSize: 11, fontFamily: 'monospace' }}>{t.db_name}</div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5, color: '#94a3b8' }}>
                <span style={{ background: st.bg, color: st.color, fontWeight: 700, fontSize: 11.5, padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase' }}>{t.status}</span>
                <span>📅 {t.status === 'trial' ? 'trial ends' : 'expires'}: <b style={{ color: dl !== null && dl < 0 ? '#f87171' : '#e2e8f0' }}>{fmtD(exp)}</b>{dl !== null && <span style={{ color: dl < 0 ? '#f87171' : dl <= 7 ? '#fbbf24' : '#64748b' }}> ({dl < 0 ? `${-dl}d ago` : `${dl}d left`})</span>}</span>
                <span>👤 {t.stats?.users ?? 0} · 🧾 {t.stats?.documents ?? 0} · 📦 {t.stats?.products ?? 0}</span>
                <span style={{ color: '#475569' }}>since {fmtD(t.created_at)}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {[['ai_analysis', '🩺 AI Check'], ['ai_scan', '📷 AI Scan'], ['ai_chat', '💬 AI Chat']].map(([k, label]) => {
                  const on = t.addons?.[k] === true;
                  return (
                    <button key={k} disabled={busy} onClick={() => setAddon(t, k, !on)} title={on ? 'Click to turn OFF' : 'Click to turn ON'}
                      style={{ background: on ? '#1d4ed8' : '#1e293b', color: on ? '#fff' : '#94a3b8', border: '1px solid ' + (on ? '#1d4ed8' : '#334155'), borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {label}: {on ? 'ON' : 'off'}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button disabled={busy} onClick={() => activate(t)} style={{ background: GREEN, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>💳 Activate</button>
                {t.status !== 'suspended'
                  ? <button disabled={busy} onClick={() => setStatus(t, 'suspended')} style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>⏸ Suspend</button>
                  : <button disabled={busy} onClick={() => setStatus(t, 'trial')} style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>▶ Re-trial</button>}
                <button disabled={busy} onClick={() => remove(t)} style={{ background: '#7f1d1d', color: '#fecaca', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>🗑 Delete DB</button>
              </div>
            </div>
          );
        })}
        {!loading && tenants && list.length === 0 && <div style={{ color: '#64748b', padding: 30, textAlign: 'center' }}>No tenants match.</div>}
      </div>
    </div>
  );
}
